import { escapeXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ RATE TABLE CONFIGURATION ============
const RATE_TABLE_ENTRY_TYPES = {
  'RATETABLETSENTRY': 'RATETABLETSENTRIES',
  'RATETABLEPOENTRY': 'RATETABLEPOENTRIES',
  'RATETABLEAPENTRY': 'RATETABLEAPENTRIES',
  'RATETABLEGLENTRY': 'RATETABLEGLENTRIES',
  'RATETABLECCENTRY': 'RATETABLECCENTRIES',
  'RATETABLEEXPENSEENTRY': 'RATETABLEEXPENSEENTRIES',
};
const RATE_TABLE_PARENT_FIELDS = ['RATETABLEID', 'NAME', 'DESCRIPTION', 'STATUS'];
const RATE_TABLE_EXCLUDED_FIELDS = ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY'];

const shouldExcludeFromEntry = (key) => {
  const upperKey = key.toUpperCase();
  if (RATE_TABLE_PARENT_FIELDS.includes(upperKey)) return true;
  if (RATE_TABLE_EXCLUDED_FIELDS.includes(upperKey)) return true;
  if (RATE_TABLE_EXCLUDED_FIELDS.some(f => upperKey.endsWith(f))) return true;
  return false;
};

// ============ DETECTION ============
export const isRateTableEntryType = (objectName) => {
  return Object.keys(RATE_TABLE_ENTRY_TYPES).includes(objectName?.toUpperCase());
};

// ============ UPDATE FUNCTIONS ============
export const getRateTableUpdateSummary = (entryObjectName, records) => {
  const containerName = RATE_TABLE_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  const grouped = {};
  const missingId = [];
  
  records.forEach((rec, idx) => {
    const rateTableId = rec.RATETABLEID;
    if (!rateTableId) { missingId.push(idx + 2); return; }
    if (!grouped[rateTableId]) grouped[rateTableId] = 0;
    grouped[rateTableId]++;
  });
  
  return {
    containerName, entryTypeName: entryObjectName,
    rateTables: Object.entries(grouped).map(([id, count]) => ({ id, entryCount: count })),
    missingIdRows: missingId, totalEntries: records.length, totalRateTables: Object.keys(grouped).length
  };
};

export const buildRateTablePreviewXml = (entryObjectName, records) => {
  const containerName = RATE_TABLE_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!containerName) return '';
  
  const grouped = {};
  records.forEach(rec => { const id = rec.RATETABLEID; if (!id) return; if (!grouped[id]) grouped[id] = []; grouped[id].push(rec); });
  const rateTableIds = Object.keys(grouped);
  if (rateTableIds.length === 0) return '<!-- No valid records with RATETABLEID -->';
  
  const firstId = rateTableIds[0];
  const firstGroupRecords = grouped[firstId];
  const parentFields = {};
  RATE_TABLE_PARENT_FIELDS.forEach(field => { const val = firstGroupRecords[0]?.[field]; if (val !== undefined && val !== '') parentFields[field] = val; });
  const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('\n      ');
  
  const sampleEntries = firstGroupRecords.slice(0, 2);
  const entriesXml = sampleEntries.map(entry => {
    const entryFields = Object.entries(entry).filter(([k, v]) => !shouldExcludeFromEntry(k) && v !== '' && v !== undefined && v !== null).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('\n          ');
    return `<${entryObjectName}>\n          ${entryFields}\n        </${entryObjectName}>`;
  }).join('\n        ');
  
  const moreEntriesNote = firstGroupRecords.length > 2 ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more entries ... -->` : '';
  const moreTablesNote = rateTableIds.length > 1 ? `\n\n<!-- ... and ${rateTableIds.length - 1} more rate table(s) will be updated ... -->` : '';
  
  return `<function controlid="${escapeXml(firstId)}">\n  <update>\n    <RATETABLE>\n      ${parentXml}\n      <${containerName}>\n        ${entriesXml}${moreEntriesNote}\n      </${containerName}>\n    </RATETABLE>\n  </update>\n</function>${moreTablesNote}`;
};

export const writeRateTableEntries = async (config, entryObjectName, records) => {
  const containerName = RATE_TABLE_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!containerName) throw new Error(`Unknown rate table entry type: ${entryObjectName}`);
  
  const missingId = records.filter(r => !r.RATETABLEID);
  if (missingId.length > 0) throw new Error(`${missingId.length} record(s) missing RATETABLEID.`);
  
  const grouped = {};
  records.forEach(rec => { const id = rec.RATETABLEID; if (!grouped[id]) grouped[id] = []; grouped[id].push(rec); });
  
  const functions = Object.entries(grouped).map(([rateTableId, entries]) => {
    const parentFields = {};
    RATE_TABLE_PARENT_FIELDS.forEach(field => { const val = entries[0][field]; if (val !== undefined && val !== '' && val !== null) parentFields[field] = val; });
    if (!parentFields.RATETABLEID) parentFields.RATETABLEID = rateTableId;
    
    const entriesXml = entries.map(entry => {
      const entryFields = {};
      Object.entries(entry).forEach(([key, val]) => { if (shouldExcludeFromEntry(key)) return; if (val === '' || val === undefined || val === null) return; entryFields[key] = val; });
      const fieldsXml = Object.entries(entryFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
      return `<${entryObjectName}>${fieldsXml}</${entryObjectName}>`;
    }).join('');
    
    const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
    return `<function controlid="${escapeXml(String(rateTableId))}"><update><RATETABLE>${parentXml}<${containerName}>${entriesXml}</${containerName}></RATETABLE></update></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, 'RATETABLE', xmlPayload, 'RATE TABLE UPDATE');
  return result.results;
};

// ============ CREATE FUNCTIONS ============
export const getRateTableCreateSummary = (entryObjectName, records) => {
  const containerName = RATE_TABLE_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  const grouped = {};
  const missingId = [];
  
  records.forEach((rec, idx) => {
    const rateTableId = rec.RATETABLEID;
    if (!rateTableId) { missingId.push(idx + 2); return; }
    if (!grouped[rateTableId]) grouped[rateTableId] = { entries: 0, hasName: false };
    grouped[rateTableId].entries++;
    if (rec.NAME) grouped[rateTableId].hasName = true;
  });
  
  const missingName = Object.entries(grouped).filter(([id, info]) => !info.hasName).map(([id]) => id);
  
  return {
    containerName, 
    entryTypeName: entryObjectName,
    rateTables: Object.entries(grouped).map(([id, info]) => ({ id, entryCount: info.entries })),
    missingIdRows: missingId, 
    missingNameTables: missingName,
    totalEntries: records.length, 
    totalRateTables: Object.keys(grouped).length
  };
};

export const buildRateTableCreatePreviewXml = (entryObjectName, records) => {
  const containerName = RATE_TABLE_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!containerName) return '';
  
  const grouped = {};
  records.forEach(rec => { 
    const id = rec.RATETABLEID; 
    if (!id) return; 
    if (!grouped[id]) grouped[id] = []; 
    grouped[id].push(rec); 
  });
  const rateTableIds = Object.keys(grouped);
  if (rateTableIds.length === 0) return '<!-- No valid records with RATETABLEID -->';
  
  const firstId = rateTableIds[0];
  const firstGroupRecords = grouped[firstId];
  const parentFields = {};
  RATE_TABLE_PARENT_FIELDS.forEach(field => { 
    const val = firstGroupRecords[0]?.[field]; 
    if (val !== undefined && val !== '') parentFields[field] = val; 
  });
  const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('\n      ');
  
  const sampleEntries = firstGroupRecords.slice(0, 2);
  const entriesXml = sampleEntries.map(entry => {
    const entryFields = Object.entries(entry)
      .filter(([k, v]) => !shouldExcludeFromEntry(k) && v !== '' && v !== undefined && v !== null)
      .map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`)
      .join('\n          ');
    return `<${entryObjectName}>\n          ${entryFields}\n        </${entryObjectName}>`;
  }).join('\n        ');
  
  const moreEntriesNote = firstGroupRecords.length > 2 ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more entries ... -->` : '';
  const moreTablesNote = rateTableIds.length > 1 ? `\n\n<!-- ... and ${rateTableIds.length - 1} more rate table(s) will be created ... -->` : '';
  
  return `<function controlid="${escapeXml(firstId)}">
  <create>
    <RATETABLE>
      ${parentXml}
      <${containerName}>
        ${entriesXml}${moreEntriesNote}
      </${containerName}>
    </RATETABLE>
  </create>
</function>${moreTablesNote}`;
};

export const createRateTableEntries = async (config, entryObjectName, records) => {
  const containerName = RATE_TABLE_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!containerName) throw new Error(`Unknown rate table entry type: ${entryObjectName}`);
  
  const missingId = records.filter(r => !r.RATETABLEID);
  if (missingId.length > 0) throw new Error(`${missingId.length} record(s) missing RATETABLEID.`);
  
  const grouped = {};
  records.forEach(rec => { 
    const id = rec.RATETABLEID; 
    if (!grouped[id]) grouped[id] = []; 
    grouped[id].push(rec); 
  });
  
  for (const [rateTableId, entries] of Object.entries(grouped)) {
    const hasName = entries.some(e => e.NAME);
    if (!hasName) throw new Error(`Rate table '${rateTableId}' is missing required NAME field.`);
  }
  
  const functions = Object.entries(grouped).map(([rateTableId, entries]) => {
    const parentFields = {};
    RATE_TABLE_PARENT_FIELDS.forEach(field => { 
      const val = entries[0][field]; 
      if (val !== undefined && val !== '' && val !== null) parentFields[field] = val; 
    });
    if (!parentFields.RATETABLEID) parentFields.RATETABLEID = rateTableId;
    
    const entriesXml = entries.map(entry => {
      const entryFields = {};
      Object.entries(entry).forEach(([key, val]) => { 
        if (shouldExcludeFromEntry(key)) return; 
        if (val === '' || val === undefined || val === null) return; 
        entryFields[key] = val; 
      });
      const fieldsXml = Object.entries(entryFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
      return `<${entryObjectName}>${fieldsXml}</${entryObjectName}>`;
    }).join('');
    
    const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
    return `<function controlid="${escapeXml(String(rateTableId))}"><create><RATETABLE>${parentXml}<${containerName}>${entriesXml}</${containerName}></RATETABLE></create></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, 'RATETABLE', xmlPayload, 'RATE TABLE CREATE');
  return result.results;
};
